import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useApolloClient } from '@apollo/react-hooks'
import { LoaderCircle, Split } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import * as Query from '../../../api/gql/query'
import ItemPicker from '../components/ItemPicker'
import MoneyInput from '../components/MoneyInput'
import MoneyLinesEditor from '../components/MoneyLinesEditor'
import { compareMoneyStrings, draftFromLines, sameLines, type DraftLine } from '../lib/contracts'
import { describeCostControlError } from '../lib/errors'
import { checkInvoiceDraftLines } from '../lib/invoices'
import { formatMoney } from '../lib/money'
import type { CostTree } from '../lib/tree'
import type { CostAllocationLine, CostContractItem, CostInvoice, CostLineInput, CostLineView } from '../types'

interface Props {
  open: boolean
  invoice: CostInvoice
  lines: CostLineView[]
  contractItems: CostContractItem[]
  tree: CostTree
  currency: string
  locale: string
  onOpenChange: (open: boolean) => void
  onSave: (lines: CostLineInput[]) => Promise<boolean>
}

/** Ruling Q3: only the contract's items; "Rozpočítat podle smlouvy" replaces the lines with the server's pro-rata split. */
const InvoiceLinesDialog: React.FC<Props> = ({ open, invoice, lines, contractItems, tree, currency, locale, onOpenChange, onSave }) => {
  const { t } = useTranslation()
  const client = useApolloClient()
  const [draft, setDraft] = useState<DraftLine[]>([])
  const [saving, setSaving] = useState(false)
  const [amount, setAmount] = useState<string | null>(null)
  const [amountValid, setAmountValid] = useState(true)
  const [splitting, setSplitting] = useState(false)
  const [splitError, setSplitError] = useState<string | null>(null)
  const saved = lines.map((l) => ({ nodeId: l.nodeId, amount: l.amount, description: null }))
  const allowed = contractItems.map((c) => c.nodeId)

  useEffect(() => {
    if (!open) return
    setDraft(draftFromLines(saved))
    setAmount(invoice.amount)
    setAmountValid(true)
    setSaving(false)
    setSplitError(null)
    // Only on opening: a background reload must not wipe the edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const check = checkInvoiceDraftLines(draft, allowed)
  const unchanged = sameLines(draft, saved)
  const busy = saving || splitting
  const canSplit = amountValid && amount !== null && compareMoneyStrings(amount, '0') > 0

  const split = async () => {
    if (!canSplit || busy) return
    setSplitting(true)
    setSplitError(null)
    try {
      const { data } = await client.query<{ costInvoicePrefill: CostAllocationLine[] }>({
        query: Query.COST_INVOICE_PREFILL,
        variables: { contractId: invoice.contractId, amount },
        fetchPolicy: 'network-only',
        context: { handlesErrors: true },
      })
      setDraft(data.costInvoicePrefill.map((l) => ({ nodeId: l.nodeId, amount: l.amount, valid: true, description: '' })))
    } catch (e: any) {
      setSplitError(describeCostControlError(e?.message, t))
    } finally {
      setSplitting(false)
    }
  }
  const save = async () => {
    if (check.problem || unchanged || busy) return
    setSaving(true)
    const ok = await onSave(draft.map((l) => ({ nodeId: l.nodeId, amount: l.amount as string })))
    setSaving(false)
    if (ok) onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('CostControl_invoice_lines_dialog_title')}</DialogTitle>
          <DialogDescription>{t('CostControl_invoice_lines_hint')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="cc-inv-split">{`${t('CostControl_invoice_prefill_amount')} (${currency})`}</Label>
            <MoneyInput id="cc-inv-split" className="h-8 w-44" value={amount} disabled={busy} onChange={(v, valid) => { setAmount(v); setAmountValid(valid) }} />
          </div>
          <Button variant="outline" size="sm" className="h-8" onClick={split} disabled={!canSplit || busy}>
            {splitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Split className="h-4 w-4" />}
            {t('CostControl_invoice_prefill')}
          </Button>
          <ItemPicker tree={tree} excludeIds={draft.map((l) => l.nodeId)} onlyIds={allowed} disabled={busy} onPick={(nodeId) => setDraft((d) => [...d, { nodeId, amount: null, valid: true, description: '' }])} />
        </div>
        {splitError && <p className="text-sm text-destructive">{splitError}</p>}
        <MoneyLinesEditor lines={draft} tree={tree} disabled={busy} showNote={false} onChange={setDraft} />
        <div className="flex justify-end text-sm">
          <span>{t('CostControl_invoice_lines_total')}: <strong className="tabular-nums">{formatMoney(check.total, { locale, currency })}</strong></span>
        </div>
        {check.problem && <p className="text-sm text-destructive">{t(check.problem)}</p>}
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('Common_cancel')}</Button>
          <Button onClick={save} disabled={busy || !!check.problem || unchanged}>
            {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Common_save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default InvoiceLinesDialog
